import React, { useState, useEffect, useRef, useMemo } from "react";
import { Income, ExpenseSection } from "../../lib/types";
import { incomeUtils } from "../../lib";
import CreatedViaBadge from "../common/CreatedViaBadge";
import { useTheme } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShareIcon from "@mui/icons-material/Share";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import RepeatIcon from "@mui/icons-material/Repeat";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { TpcInvoiceLink } from "./TpcInvoiceLink";

interface IncomeListProps {
  income: Record<ExpenseSection, Income[]>;
  sectionTotals: { section: ExpenseSection; total: number }[];
  pastMonths?: Array<{
    key: string;
    label: string;
    items: Income[];
    total: number;
  }>;
  prospectiveIncome?: Income[];
  onDeleteIncome: (income: Income) => void;
  onShareIncome?: (income: Income) => void;
  onMarkReceived: (income: Income) => void;
  onSkipIncome: (income: Income) => void;
  onEditIncome: (income: Income) => void;
  onSuspendIncome?: (income: Income) => void;
  onRescheduleIncome?: (income: Income) => void;
  expandedSection?: ExpenseSection | null;
  onSectionChange?: (section: ExpenseSection | null) => void;
  allExpanded?: boolean;
  readOnly?: boolean; // Optional: hide all action buttons (for shared views)
  showEmptySections?: boolean;
}

// Function to get dynamic section titles
const getSectionTitles = (): Record<ExpenseSection, string> => ({
  received: "Received",
  pendingDeposit: "Pending Deposit",
  overdue: "Overdue",
  today: "Today",
  tomorrow: "Tomorrow",
  thisWeek: "Within 7 Days",
  next10Days: "Within 10 Days",
  thisMonth: "Rest of the Month",
  nextMonth: `Next Month (${incomeUtils.getNextMonthName()})`,
  expected: "Next 3 Months",
  upcoming: "Next 3 Months",
});

const sectionColors: Record<ExpenseSection, string> = {
  received: "#9CA3AF", // Muted gray
  pendingDeposit: "#10B981", // Green for incoming
  overdue: "#22C55E", // Green for incoming
  today: "#34D399", // Green
  tomorrow: "#10B981", // Green
  thisWeek: "#059669", // Green
  next10Days: "#047857", // Green
  thisMonth: "#065F46", // Green
  nextMonth: "#064E3B", // Green
  expected: "#14532D", // Green
  upcoming: "#14532D", // Green
};

const IncomeList: React.FC<IncomeListProps> = ({
  income,
  sectionTotals,
  pastMonths = [],
  onDeleteIncome,
  onShareIncome,
  onMarkReceived,
  onSkipIncome,
  onEditIncome,
  onSuspendIncome,
  onRescheduleIncome,
  prospectiveIncome = [],
  expandedSection,
  onSectionChange,
  allExpanded,
  readOnly = false,
  showEmptySections = false,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  // Rebuilt on every render otherwise, and this component re-renders on each
  // accordion toggle. The debug line went with it — it fired per render with
  // hundreds of rows on screen.
  const sectionTitles = useMemo(() => getSectionTitles(), []);

  // State for collapsed sections - all collapsed by default
  const [collapsedSections, setCollapsedSections] = useState<
    Set<ExpenseSection>
  >(
    new Set([
      "received",
      "pendingDeposit",
      "overdue",
      "today",
      "tomorrow",
      "thisWeek",
      "next10Days",
      "thisMonth",
      "nextMonth",
      "expected",
    ] as ExpenseSection[])
  );
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [prospectiveCollapsed, setProspectiveCollapsed] = useState(false);
  const [pastMonthsCollapsed, setPastMonthsCollapsed] = useState(true);
  const seededPastMonthsCollapsedRef = useRef(false);

  useEffect(() => {
    if (seededPastMonthsCollapsedRef.current) return;
    if (!pastMonths || pastMonths.length === 0) return;

    // Default: keep all Past Months accordions collapsed (months and payees
    // with 2+ items) so the user starts with a totals-only view.
    const collapsedKeys: string[] = [];
    pastMonths.forEach((month) => {
      if (!month.items || month.items.length === 0) return;
      collapsedKeys.push(`pastMonths:${month.key}`);
      const groupedByPayee = month.items.reduce(
        (acc, item) => {
          const key = item.client || "Unassigned";
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        },
        {} as Record<string, Income[]>
      );
      Object.entries(groupedByPayee).forEach(([payeeKey, items]) => {
        if (items.length > 1) {
          collapsedKeys.push(`pastMonths:${month.key}:${payeeKey}`);
        }
      });
    });

    if (collapsedKeys.length > 0) {
      setCollapsedGroups((prev) => {
        const next = new Set(prev);
        collapsedKeys.forEach((k) => next.add(k));
        return next;
      });
    }

    seededPastMonthsCollapsedRef.current = true;
  }, [pastMonths]);

  // Handle external expandedSection prop changes (from card clicks)
  useEffect(() => {
    if (expandedSection !== undefined) {
      // Create a set with all sections collapsed
      const allSections: ExpenseSection[] = [
        "received",
        "pendingDeposit",
        "overdue",
        "today",
        "tomorrow",
        "thisWeek",
        "next10Days",
        "thisMonth",
        "nextMonth",
        "expected",
      ];
      const newCollapsed = new Set(allSections);

      // If a section is specified, expand only that one
      if (expandedSection !== null) {
        newCollapsed.delete(expandedSection);
        // Collapse all interior groups for the expanded section
        const newCollapsedGroups = new Set<string>();
        const sectionIncome = income[expandedSection];
        if (sectionIncome) {
          const groupedByClient = sectionIncome.reduce(
            (acc, item) => {
              const key = item.client || "Unassigned";
              if (!acc[key]) acc[key] = [];
              acc[key].push(item);
              return acc;
            },
            {} as Record<string, Income[]>
          );
          Object.keys(groupedByClient).forEach((groupKey) => {
            newCollapsedGroups.add(`${expandedSection}:${groupKey}`);
          });
        }
        setCollapsedGroups(newCollapsedGroups);
      }
      setCollapsedSections(newCollapsed);
    }
  }, [expandedSection, income]);

  // Handle allExpanded prop changes (from collapse/expand all toggle)
  useEffect(() => {
    if (allExpanded !== undefined) {
      const allSections: ExpenseSection[] = [
        "received",
        "pendingDeposit",
        "overdue",
        "today",
        "tomorrow",
        "thisWeek",
        "next10Days",
        "thisMonth",
        "nextMonth",
        "expected",
      ];

      if (allExpanded) {
        // Expand all: clear the collapsed sections set
        setCollapsedSections(new Set());
      } else {
        // Collapse all: add all sections to the collapsed set
        setCollapsedSections(new Set(allSections));
      }
      // Also reset collapsed groups
      setCollapsedGroups(new Set());
    }
  }, [allExpanded]);

  const toggleSection = (section: ExpenseSection) => {
    const newCollapsed = new Set(collapsedSections);
    const isExpanding = newCollapsed.has(section);
    if (isExpanding) {
      newCollapsed.delete(section);
      // Collapse all interior groups when expanding a section
      const newCollapsedGroups = new Set(collapsedGroups);
      // Collapse all groups within this section
      Array.from(collapsedGroups).forEach((key) => {
        if (key.startsWith(`${section}:`)) {
          newCollapsedGroups.delete(key);
        }
      });
      // Add all groups as collapsed for this section
      const sectionIncome = income[section];
      const groupedByClient = sectionIncome.reduce(
        (acc, item) => {
          const key = item.client || "Unassigned";
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        },
        {} as Record<string, Income[]>
      );
      Object.keys(groupedByClient).forEach((groupKey) => {
        newCollapsedGroups.add(`${section}:${groupKey}`);
      });
      setCollapsedGroups(newCollapsedGroups);
    } else {
      newCollapsed.add(section);
    }
    setCollapsedSections(newCollapsed);
  };

  const toggleGroup = (section: ExpenseSection, key: string) => {
    const groupKey = `${section}:${key}`;
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupKey)) {
      newCollapsed.delete(groupKey);
    } else {
      newCollapsed.add(groupKey);
    }
    setCollapsedGroups(newCollapsed);
  };

  const toggleProspectiveGroup = (key: string) => {
    const groupKey = `prospective:${key}`;
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupKey)) {
      newCollapsed.delete(groupKey);
    } else {
      newCollapsed.add(groupKey);
    }
    setCollapsedGroups(newCollapsed);
  };

  const togglePastMonth = (key: string) => {
    const groupKey = `pastMonths:${key}`;
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupKey)) {
      newCollapsed.delete(groupKey);
    } else {
      newCollapsed.add(groupKey);
    }
    setCollapsedGroups(newCollapsed);
  };

  const togglePastMonthPayeeGroup = (monthKey: string, payeeKey: string) => {
    const groupKey = `pastMonths:${monthKey}:${payeeKey}`;
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupKey)) {
      newCollapsed.delete(groupKey);
    } else {
      newCollapsed.add(groupKey);
    }
    setCollapsedGroups(newCollapsed);
  };
  // Sort sections by priority for display
  const sectionOrder: ExpenseSection[] = [
    "received",
    "pendingDeposit",
    "overdue",
    "today",
    "tomorrow",
    "thisWeek",
    "next10Days",
    "thisMonth",
    "nextMonth",
    "expected",
  ];

  // Define section groups
  const thisMonthSections: ExpenseSection[] = [
    "received",
    "pendingDeposit",
    "overdue",
    "today",
    "tomorrow",
    "thisWeek",
    "next10Days",
    "thisMonth",
  ];
  const futureSections: ExpenseSection[] = ["nextMonth", "expected"];

  // Check if a group has any items
  const hasThisMonthItems = thisMonthSections.some(
    (s) => income[s] && income[s].length > 0
  );
  const hasFutureItems = futureSections.some(
    (s) => income[s] && income[s].length > 0
  );

  // Section group heading style
  const groupHeadingStyle = {
    fontSize: "14px",
    fontWeight: 600,
    color: "#718096",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    marginBottom: "16px",
    marginTop: "8px",
    paddingBottom: "8px",
    borderBottom: isDarkMode ? "1px solid #374151" : "1px solid #E2E8F0",
  };

  const sectionContainerStyle = {
    marginBottom: "20px",
  };

  const getAccordionValueColor = (section: ExpenseSection) =>
    section === "received"
      ? isDarkMode
        ? "rgba(226, 232, 240, 0.65)"
        : "rgba(45, 55, 72, 0.65)"
      : "#10B981";

  const sectionHeaderStyle = (isCollapsed: boolean) => ({
    backgroundColor: isDarkMode ? "#1a1a1a" : "white",
    padding: "10px",
    borderRadius: isCollapsed ? "8px" : "8px 8px 0 0",
    boxShadow: isDarkMode
      ? "0 1px 3px rgba(0,0,0,0.5)"
      : "0 1px 3px rgba(0,0,0,0.1)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  });

  const sectionContentStyle = {
    backgroundColor: isDarkMode ? "#252525" : "#f7f8fa",
    // Tighter than before, but keeps the first row off the section header.
    padding: "10px 10px 8px",
    borderRadius: "0 0 8px 8px",
    boxShadow: isDarkMode
      ? "0 1px 3px rgba(0,0,0,0.5)"
      : "0 1px 3px rgba(0,0,0,0.1)",
  };

  const headingStyle = {
    fontSize: "18px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
  };

  const groupItemsContainerStyle = {
    backgroundColor: isDarkMode ? "#1b1b1b" : "#eef2f7",
    border: isDarkMode ? "1px solid #2a2a2a" : "1px solid #d7dee8",
    padding: "6px",
    borderRadius: "0 0 8px 8px",
    marginBottom: "8px",
  };

  const badgeStyle = (color: string) => ({
    backgroundColor: `${color}20`,
    color: color,
    fontSize: "12px",
    fontWeight: "bold",
    padding: "2px 8px",
    borderRadius: "9999px",
    marginLeft: "8px",
  });

  const incomeItemStyle = {
    backgroundColor: isDarkMode ? "#141414" : "#ffffff",
    border: isDarkMode ? "1px solid #2a2a2a" : "1px solid #e2e8f0",
    padding: "6px 10px",
    borderRadius: "6px",
    boxShadow: isDarkMode
      ? "0 2px 4px rgba(0, 0, 0, 0.3)"
      : "0 1px 2px rgba(0,0,0,0.05)",
    marginBottom: "6px",
  };

  const pastMonthsPalette = {
    // Each deeper level is slightly lighter than its parent.
    header: isDarkMode ? "#1a1a1a" : "#e9eef5",
    content: isDarkMode ? "#252525" : "#f1f5f9",
    monthHeader: isDarkMode ? "#2b2b2b" : "#f3f6fb",
    payeeHeader: isDarkMode ? "#313131" : "#f8fafc",
    itemsContainer: isDarkMode ? "#373737" : "#ffffff",
    item: isDarkMode ? "#3d3d3d" : "#ffffff",
    border: isDarkMode ? "#4a4a4a" : "#d7dee8",
  };

  const pastMonthsContentStyle = {
    ...sectionContentStyle,
    backgroundColor: pastMonthsPalette.content,
  };

  const pastMonthHeaderStyle = (collapsed: boolean) => ({
    ...sectionHeaderStyle(collapsed),
    backgroundColor: pastMonthsPalette.monthHeader,
  });

  const pastPayeeHeaderStyle = (collapsed: boolean) => ({
    ...sectionHeaderStyle(collapsed),
    backgroundColor: pastMonthsPalette.payeeHeader,
  });

  const pastItemsContainerStyle = {
    backgroundColor: pastMonthsPalette.itemsContainer,
    border: `1px solid ${pastMonthsPalette.border}`,
    padding: "6px",
    borderRadius: "0 0 8px 8px",
    marginBottom: 0,
  };

  const pastIncomeItemStyle = {
    ...incomeItemStyle,
    backgroundColor: pastMonthsPalette.item,
    border: `1px solid ${pastMonthsPalette.border}`,
  };

  const incomeRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const tagStyle = {
    backgroundColor: "#805AD520",
    color: "#805AD5",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "12px",
    marginRight: "4px",
  };

  const sharedTagStyle = {
    backgroundColor: "#3182CE20",
    color: "#3182CE",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "12px",
    marginRight: "4px",
  };

  const linkButtonStyle = {
    backgroundColor: "#38B2AC20",
    color: "#38B2AC",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "12px",
    textDecoration: "none",
    display: "inline-block",
    marginLeft: "4px",
  };

  const noteIndicatorStyle = {
    marginLeft: "6px",
    fontSize: "12px",
    color: "#4299E1",
    cursor: "help",
  };

  // Handle copying ID with animation
  const handleCopyId = (id: string) => {
    navigator.clipboard
      .writeText(id)
      .then(() => {
        console.log("[IncomeList] Copied ID:", id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
      })
      .catch((error) => {
        console.error("[IncomeList] Failed to copy ID:", error);
      });
  };

  // Icon button styles
  const titleRightClusterStyle = {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap" as const,
    justifyContent: "flex-end",
    fontWeight: "normal",
  };

  const titleRightMetaStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap" as const,
    justifyContent: "flex-end",
  };

  const titleRightIconsStyle = {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginLeft: "auto",
  };

  const titleMetaTextStyle = {
    color: "#718096",
    fontSize: "12px",
  };

  const formatRecurrenceLabel = (pattern?: string) => {
    if (!pattern) return "";
    if (pattern.startsWith("daily:")) {
      const dayMap = {
        mon: "M",
        tue: "T",
        wed: "We",
        thu: "Thu",
        fri: "Fri",
        sat: "Sat",
        sun: "Sun",
      } as Record<string, string>;
      const days = pattern
        .replace("daily:", "")
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean)
        .map((d) => dayMap[d] || d);
      return `Daily (${days.join(", ")})`;
    }
    return pattern;
  };

  // Plain buttons with a native tooltip. MUI's Tooltip/IconButton pair mounts
  // popper machinery per instance, and this list renders five of them on every
  // row — with a few hundred rows that alone cost seconds when a section opens.
  const actionButtonStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px",
    border: "none",
    background: "transparent",
    color: "#9CA3AF",
    cursor: "pointer",
    borderRadius: "4px",
    lineHeight: 0,
  };

  const renderActionButton = (
    label: string,
    onClick: () => void,
    icon: React.ReactNode,
    hoverColor: string
  ) => (
    <button
      key={label}
      type="button"
      title={label}
      aria-label={label}
      style={actionButtonStyle}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = hoverColor;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "#9CA3AF";
      }}
    >
      {icon}
    </button>
  );

  const renderItemActionIcons = (item: Income, canInteract: boolean) => {
    if (!canInteract) return null;
    return (
      <span className="income-item-actions" style={titleRightIconsStyle}>
        {renderActionButton(
          "Edit",
          () => onEditIncome(item),
          <EditIcon sx={{ fontSize: 16 }} />,
          "#4299E1"
        )}
        {renderActionButton(
          "Skip",
          () => onSkipIncome(item),
          <SkipNextIcon sx={{ fontSize: 16 }} />,
          "#4299E1"
        )}
        {onRescheduleIncome &&
          renderActionButton(
            "Move to a later date",
            () => onRescheduleIncome(item),
            <EditCalendarIcon sx={{ fontSize: 16 }} />,
            "#4299E1"
          )}
        {onSuspendIncome &&
          renderActionButton(
            incomeUtils.isIncomeSuspended(item) ? "Resume" : "Postpone",
            () => onSuspendIncome(item),
            incomeUtils.isIncomeSuspended(item) ? (
              <PlayCircleOutlineIcon sx={{ fontSize: 16 }} />
            ) : (
              <PauseCircleOutlineIcon sx={{ fontSize: 16 }} />
            ),
            "#D69E2E"
          )}
        {onShareIncome &&
          renderActionButton(
            "Share",
            () => onShareIncome(item),
            <ShareIcon sx={{ fontSize: 16 }} />,
            "#4299E1"
          )}
        {renderActionButton(
          "Delete",
          () => onDeleteIncome(item),
          <DeleteIcon sx={{ fontSize: 16 }} />,
          "#E53E3E"
        )}
      </span>
    );
  };

  // Same reasoning as above: one native-title button instead of a MUI pair.
  const renderIdButton = (item: Income) => (
    <button
      type="button"
      title={`ID: ${item.id}`}
      aria-label="Copy income id"
      style={actionButtonStyle}
      onClick={(e) => {
        e.stopPropagation();
        handleCopyId(item.id);
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
    </button>
  );

  const prospectiveItems = prospectiveIncome.filter(
    (item) => (item.type || "Actual") === "Prospective"
  );
  const prospectiveTotal = prospectiveItems.reduce(
    (sum, item) => sum + incomeUtils.getInvoiceTotal(item),
    0
  );
  const prospectiveGroupedByClient = prospectiveItems.reduce(
    (acc, item) => {
      const key = item.client || "Unassigned";
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<string, Income[]>
  );
  const prospectiveGroupEntries = Object.entries(
    prospectiveGroupedByClient
  ) as Array<[string, Income[]]>;
  const prospectiveShouldGroup =
    prospectiveItems.length > 1 && prospectiveGroupEntries.length > 0;
  const prospectiveEntries: Array<[string, Income[]]> = prospectiveShouldGroup
    ? [...prospectiveGroupEntries].sort((a, b) => {
        const totalA = a[1].reduce(
          (sum, item) => sum + incomeUtils.getInvoiceTotal(item),
          0
        );
        const totalB = b[1].reduce(
          (sum, item) => sum + incomeUtils.getInvoiceTotal(item),
          0
        );
        return totalA - totalB;
      })
    : [["__all__", prospectiveItems]];

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* CSS animation for Copied! effect */}
      <style>
        {`
          @keyframes fadeUpOut {
            0% {
              opacity: 1;
              transform: translateY(0);
            }
            100% {
              opacity: 0;
              transform: translateY(-10px);
            }
          }

          .income-item-actions {
            opacity: 0;
            transform: translateX(8px);
            transition: opacity 0.2s ease, transform 0.2s ease;
            pointer-events: none;
          }

          .income-accordion-item:hover .income-item-actions {
            opacity: 1;
            transform: translateX(0);
            pointer-events: auto;
          }

          .income-accordion-item:focus-within .income-item-actions {
            opacity: 1;
            transform: translateX(0);
            pointer-events: auto;
          }
        `}
      </style>
      {prospectiveItems.length > 0 && (
        <div style={{ ...sectionContainerStyle, order: -1 }}>
          <div
            style={{
              ...sectionHeaderStyle(prospectiveCollapsed),
              borderLeft: "4px solid #F6AD55",
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={() => setProspectiveCollapsed((prev) => !prev)}
          >
            <div style={headingStyle}>
              <span style={{ marginRight: "8px", fontSize: "14px" }}>
                {prospectiveCollapsed ? "▶" : "▼"}
              </span>
              Prospective Income
              <span style={badgeStyle("#F6AD55")}>
                {prospectiveItems.length}
              </span>
            </div>
            <div style={{ fontWeight: "bold" }}>
              {incomeUtils.formatCurrency(prospectiveTotal)}
            </div>
          </div>
          {!prospectiveCollapsed && (
            <div style={sectionContentStyle}>
              <div
                style={{
                  color: "#A0AEC0",
                  fontSize: "12px",
                  marginBottom: "10px",
                }}
              >
                These items are strictly prospective, not formal projections.
                For example “If I complete Project A I will get hired for
                Project B.”
              </div>
              {prospectiveEntries.map(([groupKey, items]) => {
                const isGroupCollapsed = collapsedGroups.has(
                  `prospective:${groupKey}`
                );
                const groupTotal = items.reduce(
                  (sum, item) => sum + incomeUtils.getInvoiceTotal(item),
                  0
                );
                const showGroupAccordion =
                  prospectiveShouldGroup && items.length > 1;
                return (
                  <div key={`prospective-${groupKey}`}>
                    {showGroupAccordion && (
                      <div
                        style={{
                          ...sectionHeaderStyle(isGroupCollapsed),
                          marginBottom: 0,
                          borderLeft: "4px solid #F6AD55",
                          cursor: "pointer",
                        }}
                        onClick={() => toggleProspectiveGroup(groupKey)}
                      >
                        <div style={headingStyle}>
                          <span
                            style={{ marginRight: "8px", fontSize: "14px" }}
                          >
                            {isGroupCollapsed ? "▶" : "▼"}
                          </span>
                          {groupKey}
                          <span style={badgeStyle("#F6AD55")}>
                            {items.length}
                          </span>
                        </div>
                        <div style={{ fontWeight: "bold" }}>
                          {incomeUtils.formatCurrency(groupTotal)}
                        </div>
                      </div>
                    )}
                    {(!showGroupAccordion || !isGroupCollapsed) && (
                      <div style={{ marginBottom: "12px" }}>
                        {items.map((item: Income) => {
                          const isDepositGhost = Boolean(
                            (item as Income & { isDepositGhost?: boolean })
                              .isDepositGhost
                          );
                          const pendingDeposit =
                            incomeUtils.isPendingDeposit(item);
                          const canInteract = !readOnly && !isDepositGhost;
                          const resolvedItemStyle = isDepositGhost
                            ? {
                                ...incomeItemStyle,
                                opacity: 0.6,
                                backgroundColor: isDarkMode
                                  ? "#191919"
                                  : "#fbfbfb",
                              }
                            : incomeItemStyle;
                          return (
                            <div
                              key={item.id}
                              style={resolvedItemStyle}
                              className="income-accordion-item"
                            >
                              <div style={incomeRowStyle}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <div>
                                    <div
                                      style={{
                                        fontWeight: "bold",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        position: "relative",
                                        width: "100%",
                                      }}
                                      className={
                                        item.notes ? "has-tooltip" : ""
                                      }
                                    >
                                      {item.client}
                                      <CreatedViaBadge
                                        createdVia={item.createdVia}
                                      />
                                      <TpcInvoiceLink
                                        sourceProvider={item.sourceProvider}
                                        sourceDocumentId={item.sourceDocumentId}
                                        sourceDocumentType={
                                          item.sourceDocumentType
                                        }
                                        sourceClientId={item.sourceClientId}
                                      />
                                      {renderIdButton(item)}
                                      {copiedId === item.id && (
                                        <span
                                          style={{
                                            position: "absolute",
                                            left: "100%",
                                            marginLeft: "8px",
                                            fontSize: "12px",
                                            color: "#10B981",
                                            fontWeight: "normal",
                                            animation:
                                              "fadeUpOut 1.5s ease-out forwards",
                                          }}
                                        >
                                          Copied!
                                        </span>
                                      )}
                                      {item.notes && (
                                        <span style={noteIndicatorStyle}>
                                          📝
                                        </span>
                                      )}
                                      <span style={titleRightClusterStyle}>
                                        <span style={titleRightMetaStyle}>
                                          {item.recurrencePattern && (
                                            <span style={titleMetaTextStyle}>
                                              {formatRecurrenceLabel(
                                                item.recurrencePattern
                                              )}
                                            </span>
                                          )}
                                          {item.isDepositGhost && (
                                            <span style={titleMetaTextStyle}>
                                              Deposit Pending
                                            </span>
                                          )}
                                          {pendingDeposit && (
                                            <span style={titleMetaTextStyle}>
                                              Pending Deposit
                                            </span>
                                          )}
                                          {item.isShared ? (
                                            <span style={sharedTagStyle}>
                                              Shared
                                            </span>
                                          ) : null}
                                          {item.invoiceLink && (
                                            <a
                                              href={item.invoiceLink}
                                              target="_blank"
                                              rel="noreferrer"
                                              style={linkButtonStyle}
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            >
                                              View Invoice
                                            </a>
                                          )}
                                        </span>
                                        {renderItemActionIcons(
                                          item,
                                          canInteract
                                        )}
                                      </span>
                                    </div>
                                    <div style={{ color: "#718096" }}>
                                      {item.dependsOnCompletion && (
                                        <span>
                                          Depends on: {item.dependsOnCompletion}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    textAlign: "right",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                  }}
                                >
                                  <div style={{ fontWeight: "bold" }}>
                                    {incomeUtils.formatCurrency(
                                      incomeUtils.getInvoiceTotal(item)
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      color: "#718096",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Due:{" "}
                                    {item.expectedByDate
                                      ? new Date(
                                          item.expectedByDate
                                        ).toLocaleDateString()
                                      : "—"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {pastMonths.some((m) => m.items.length > 0) && (
        <div style={{ ...sectionContainerStyle, order: -2 }}>
          <div
            style={{
              ...sectionHeaderStyle(pastMonthsCollapsed),
              borderLeft: "4px solid #9CA3AF",
              cursor: "pointer",
              userSelect: "none",
              backgroundColor: pastMonthsPalette.header,
            }}
            onClick={() => setPastMonthsCollapsed((prev) => !prev)}
          >
            <div style={headingStyle}>
              <span style={{ marginRight: "8px", fontSize: "14px" }}>
                {pastMonthsCollapsed ? "▶" : "▼"}
              </span>
              Past Months
              <span style={badgeStyle("#9CA3AF")}>
                {pastMonths.reduce((sum, m) => sum + m.items.length, 0)}
              </span>
            </div>
            <div style={{ fontWeight: "bold" }}>
              {incomeUtils.formatCurrency(
                pastMonths.reduce((sum, m) => sum + m.total, 0)
              )}
            </div>
          </div>

          {!pastMonthsCollapsed && (
            <div style={pastMonthsContentStyle}>
              {pastMonths.map((month) => {
                if (month.items.length === 0) return null;
                const isMonthCollapsed = collapsedGroups.has(
                  `pastMonths:${month.key}`
                );
                return (
                  <div key={`past-${month.key}`} style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        ...pastMonthHeaderStyle(isMonthCollapsed),
                        marginBottom: 0,
                        borderLeft: "4px solid #9CA3AF",
                        cursor: "pointer",
                      }}
                      onClick={() => togglePastMonth(month.key)}
                    >
                      <div style={headingStyle}>
                        <span style={{ marginRight: "8px", fontSize: "14px" }}>
                          {isMonthCollapsed ? "▶" : "▼"}
                        </span>
                        {month.label}
                        <span style={badgeStyle("#9CA3AF")}>
                          {month.items.length}
                        </span>
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        {incomeUtils.formatCurrency(month.total)}
                      </div>
                    </div>

                    {!isMonthCollapsed && (
                      // Breathing room so the first group is not flush against
                      // the month header.
                      <div style={{ marginTop: 8, marginBottom: "12px" }}>
                        {(() => {
                          const groupedByPayee = month.items.reduce(
                            (acc, item) => {
                              const key = item.client || "Unassigned";
                              if (!acc[key]) acc[key] = [];
                              acc[key].push(item);
                              return acc;
                            },
                            {} as Record<string, Income[]>
                          );
                          const entries = Object.entries(
                            groupedByPayee
                          ) as Array<[string, Income[]]>;

                          // Sort payees by smallest total first (ascending) for quick scanning.
                          const sortedEntries = [...entries].sort((a, b) => {
                            const totalA = a[1].reduce(
                              (sum, item) =>
                                sum + incomeUtils.getInvoiceTotal(item),
                              0
                            );
                            const totalB = b[1].reduce(
                              (sum, item) =>
                                sum + incomeUtils.getInvoiceTotal(item),
                              0
                            );
                            return totalA - totalB;
                          });

                          return sortedEntries.map(([payeeKey, items]) => {
                            const payeeTotal = items.reduce(
                              (sum, item) =>
                                sum + incomeUtils.getInvoiceTotal(item),
                              0
                            );
                            const isPayeeCollapsed = collapsedGroups.has(
                              `pastMonths:${month.key}:${payeeKey}`
                            );
                            const showPayeeAccordion = items.length > 1;

                            const itemRows = items.map((item) => {
                              const canInteract = !readOnly;
                              return (
                                <div
                                  key={item.id}
                                  style={pastIncomeItemStyle}
                                  className="income-accordion-item"
                                >
                                  <div style={incomeRowStyle}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <div>
                                        <div
                                          style={{
                                            fontWeight: "bold",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            position: "relative",
                                            width: "100%",
                                          }}
                                          className={
                                            item.notes ? "has-tooltip" : ""
                                          }
                                        >
                                          {item.client}
                                          <CreatedViaBadge
                                            createdVia={item.createdVia}
                                          />
                                          <TpcInvoiceLink
                                            sourceProvider={item.sourceProvider}
                                            sourceDocumentId={
                                              item.sourceDocumentId
                                            }
                                            sourceDocumentType={
                                              item.sourceDocumentType
                                            }
                                            sourceClientId={item.sourceClientId}
                                          />
                                          {renderIdButton(item)}
                                          {copiedId === item.id && (
                                            <span
                                              style={{
                                                position: "absolute",
                                                left: "100%",
                                                marginLeft: "8px",
                                                fontSize: "12px",
                                                color: "#10B981",
                                                fontWeight: "normal",
                                                animation:
                                                  "fadeUpOut 1.5s ease-out forwards",
                                              }}
                                            >
                                              Copied!
                                            </span>
                                          )}
                                          {item.notes && (
                                            <span style={noteIndicatorStyle}>
                                              📝
                                            </span>
                                          )}
                                          <span style={titleRightClusterStyle}>
                                            <span style={titleRightMetaStyle}>
                                              {item.isShared ? (
                                                <span style={sharedTagStyle}>
                                                  Shared
                                                </span>
                                              ) : null}
                                              {item.invoiceLink && (
                                                <a
                                                  href={item.invoiceLink}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  style={linkButtonStyle}
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  View Invoice
                                                </a>
                                              )}
                                            </span>
                                            {renderItemActionIcons(
                                              item,
                                              canInteract
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: "#718096",
                                          fontSize: "12px",
                                        }}
                                      >
                                        {item.expectedByDate
                                          ? new Date(
                                              item.expectedByDate
                                            ).toLocaleDateString()
                                          : "—"}
                                      </span>
                                      <span style={{ fontWeight: "bold" }}>
                                        {incomeUtils.formatCurrency(
                                          incomeUtils.getInvoiceTotal(item)
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            });

                            const itemsContainer = (
                              <div
                                style={{
                                  ...pastItemsContainerStyle,
                                  marginLeft: "16px",
                                  marginBottom: 0,
                                }}
                              >
                                {itemRows}
                              </div>
                            );

                            if (!showPayeeAccordion) {
                              // A lone item is its own row — no accordion and
                              // no group container wrapped around it.
                              return (
                                <div
                                  key={`past-${month.key}-${payeeKey}`}
                                  style={{
                                    marginBottom: 6,
                                    marginLeft: "16px",
                                  }}
                                >
                                  {itemRows}
                                </div>
                              );
                            }

                            return (
                              <div
                                key={`past-${month.key}-${payeeKey}`}
                                style={{ marginBottom: 10 }}
                              >
                                <div
                                  style={{
                                    ...pastPayeeHeaderStyle(isPayeeCollapsed),
                                    marginBottom: 0,
                                    marginLeft: "16px",
                                    borderLeft: "4px solid #9CA3AF",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    togglePastMonthPayeeGroup(
                                      month.key,
                                      payeeKey
                                    )
                                  }
                                >
                                  <div style={headingStyle}>
                                    <span
                                      style={{
                                        marginRight: "8px",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {isPayeeCollapsed ? "▶" : "▼"}
                                    </span>
                                    {payeeKey}
                                    <span style={badgeStyle("#9CA3AF")}>
                                      {items.length}
                                    </span>
                                  </div>
                                  <div style={{ fontWeight: "bold" }}>
                                    {incomeUtils.formatCurrency(payeeTotal)}
                                  </div>
                                </div>

                                {!isPayeeCollapsed && itemsContainer}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {sectionOrder.map((section, index) => {
        const sectionIncome = income[section];
        const total =
          sectionTotals.find((s) => s.section === section)?.total || 0;
        const isCollapsed = collapsedSections.has(section);
        const groupedByClient = sectionIncome.reduce(
          (acc, item) => {
            const key = item.client || "Unassigned";
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
          },
          {} as Record<string, Income[]>
        );
        const groupEntries = Object.entries(groupedByClient) as Array<
          [string, Income[]]
        >;
        const shouldGroup = sectionIncome.length > 1 && groupEntries.length > 0;
        // Sort entries by smallest income first (ascending by total)
        const sortedGroupEntries = [...groupEntries].sort((a, b) => {
          const totalA = a[1].reduce(
            (sum, item) => sum + incomeUtils.getInvoiceTotal(item),
            0
          );
          const totalB = b[1].reduce(
            (sum, item) => sum + incomeUtils.getInvoiceTotal(item),
            0
          );
          return totalA - totalB;
        });
        const entries: Array<[string, Income[]]> = shouldGroup
          ? sortedGroupEntries
          : [["__all__", sectionIncome]];

        // Skip empty sections unless explicitly requested
        if (sectionIncome.length === 0 && !showEmptySections) return null;

        // Determine if we need to show a group heading
        const isFirstThisMonthSection =
          thisMonthSections.includes(section) &&
          thisMonthSections
            .slice(0, thisMonthSections.indexOf(section))
            .every((s) => !income[s] || income[s].length === 0);
        const isFirstFutureSection =
          futureSections.includes(section) &&
          futureSections
            .slice(0, futureSections.indexOf(section))
            .every((s) => !income[s] || income[s].length === 0);

        // Add muted styling for received section
        const isReceivedSection = section === "received";
        const itemBaseStyle = isReceivedSection
          ? {
              ...incomeItemStyle,
              opacity: 0.7,
              backgroundColor: isDarkMode ? "#1f1f1f" : "#f8f9fa",
            }
          : incomeItemStyle;

        return (
          <React.Fragment key={section}>
            {/* This Month group heading */}
            {isFirstThisMonthSection && hasThisMonthItems && (
              <div style={groupHeadingStyle}>This Month</div>
            )}
            {/* Future group heading */}
            {isFirstFutureSection && hasFutureItems && (
              <div style={groupHeadingStyle}>Future</div>
            )}
            <div style={sectionContainerStyle}>
              <div
                style={{
                  ...sectionHeaderStyle(isCollapsed),
                  borderLeft: `4px solid ${sectionColors[section]}`,
                  opacity: isReceivedSection ? 0.8 : 1,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => toggleSection(section)}
              >
                <div style={headingStyle}>
                  <span style={{ marginRight: "8px", fontSize: "14px" }}>
                    {isCollapsed ? "▶" : "▼"}
                  </span>
                  {sectionTitles[section]}
                  <span style={badgeStyle(sectionColors[section])}>
                    {sectionIncome.length}
                  </span>
                </div>
                <div
                  style={{
                    fontWeight: "bold",
                    color: getAccordionValueColor(section),
                  }}
                >
                  {incomeUtils.formatCurrency(total)}
                </div>
              </div>

              {!isCollapsed && (
                <div style={sectionContentStyle}>
                  {entries.map(([groupKey, items]) => {
                    const isGroupCollapsed = collapsedGroups.has(
                      `${section}:${groupKey}`
                    );
                    const groupTotal = items.reduce((sum, item) => {
                      if (
                        isReceivedSection &&
                        incomeUtils.isPendingDeposit(item)
                      ) {
                        return sum;
                      }
                      return sum + incomeUtils.getInvoiceTotal(item);
                    }, 0);
                    // Only show group accordion if there are multiple items in the group
                    const showGroupAccordion = shouldGroup && items.length > 1;
                    return (
                      <div key={`${section}-${groupKey}`}>
                        {showGroupAccordion && (
                          <div
                            style={{
                              ...sectionHeaderStyle(isGroupCollapsed),
                              // When expanded the header connects to the items
                              // container directly below it (no gap). When
                              // collapsed there is no container below, so it
                              // needs bottom spacing or the next group/item
                              // touches it.
                              marginBottom: isGroupCollapsed ? "12px" : 0,
                              borderLeft: `4px solid ${sectionColors[section]}`,
                              cursor: "pointer",
                            }}
                            onClick={() => toggleGroup(section, groupKey)}
                          >
                            <div style={headingStyle}>
                              <span
                                style={{ marginRight: "8px", fontSize: "14px" }}
                              >
                                {isGroupCollapsed ? "▶" : "▼"}
                              </span>
                              {groupKey}
                              <span style={badgeStyle(sectionColors[section])}>
                                {items.length}
                              </span>
                            </div>
                            <div
                              style={{
                                fontWeight: "bold",
                                color: getAccordionValueColor(section),
                              }}
                            >
                              {incomeUtils.formatCurrency(groupTotal)}
                            </div>
                          </div>
                        )}
                        {(!showGroupAccordion || !isGroupCollapsed) && (
                          <div
                            style={
                              showGroupAccordion
                                ? groupItemsContainerStyle
                                : { marginBottom: "12px" }
                            }
                          >
                            {items.map((item: Income) => {
                              const isDepositGhost = Boolean(
                                (item as Income & { isDepositGhost?: boolean })
                                  .isDepositGhost
                              );
                              const pendingDeposit =
                                incomeUtils.isPendingDeposit(item);
                              const canInteract = !readOnly && !isDepositGhost;
                              const resolvedItemStyle = isDepositGhost
                                ? {
                                    ...itemBaseStyle,
                                    opacity: 0.6,
                                    backgroundColor: isDarkMode
                                      ? "#191919"
                                      : "#fbfbfb",
                                  }
                                : itemBaseStyle;
                              return (
                                <div
                                  key={item.id}
                                  style={resolvedItemStyle}
                                  className="income-accordion-item"
                                >
                                  <div style={incomeRowStyle}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <div>
                                        <div
                                          style={{
                                            fontWeight: "bold",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            position: "relative",
                                            width: "100%",
                                          }}
                                          className={
                                            item.notes ? "has-tooltip" : ""
                                          }
                                        >
                                          {item.client}
                                          <CreatedViaBadge
                                            createdVia={item.createdVia}
                                          />
                                          <TpcInvoiceLink
                                            sourceProvider={item.sourceProvider}
                                            sourceDocumentId={
                                              item.sourceDocumentId
                                            }
                                            sourceDocumentType={
                                              item.sourceDocumentType
                                            }
                                            sourceClientId={item.sourceClientId}
                                          />
                                          {renderIdButton(item)}
                                          {/* Copied animation */}
                                          {copiedId === item.id && (
                                            <span
                                              style={{
                                                position: "absolute",
                                                left: "100%",
                                                marginLeft: "8px",
                                                fontSize: "12px",
                                                color: "#10B981",
                                                fontWeight: "normal",
                                                animation:
                                                  "fadeUpOut 1.5s ease-out forwards",
                                              }}
                                            >
                                              Copied!
                                            </span>
                                          )}
                                          {item.notes && (
                                            <span style={noteIndicatorStyle}>
                                              📝
                                            </span>
                                          )}
                                          <span style={titleRightClusterStyle}>
                                            <span style={titleRightMetaStyle}>
                                              <span style={titleMetaTextStyle}>
                                                {pendingDeposit &&
                                                item.depositDate
                                                  ? `Will be deposited by ${new Date(
                                                      item.depositDate
                                                    ).toLocaleString("en-US", {
                                                      month: "2-digit",
                                                      day: "2-digit",
                                                      year: "2-digit",
                                                      hour: "numeric",
                                                      minute: "2-digit",
                                                    })}`
                                                  : item.receivedDate
                                                    ? `Received: ${(() => {
                                                        const receivedDate =
                                                          new Date(
                                                            item.receivedDate
                                                          );
                                                        return `${(
                                                          receivedDate.getUTCMonth() +
                                                          1
                                                        )
                                                          .toString()
                                                          .padStart(
                                                            2,
                                                            "0"
                                                          )}/${receivedDate
                                                          .getUTCDate()
                                                          .toString()
                                                          .padStart(
                                                            2,
                                                            "0"
                                                          )}/${receivedDate.getUTCFullYear()}`;
                                                      })()}`
                                                    : isDepositGhost
                                                      ? "Processing"
                                                      : `Expected: ${(
                                                          item.expectedByDate.getUTCMonth() +
                                                          1
                                                        )
                                                          .toString()
                                                          .padStart(
                                                            2,
                                                            "0"
                                                          )}/${item.expectedByDate
                                                          .getUTCDate()
                                                          .toString()
                                                          .padStart(
                                                            2,
                                                            "0"
                                                          )}/${item.expectedByDate.getUTCFullYear()}`}
                                              </span>
                                              {item.paymentProcessor && (
                                                <span
                                                  style={titleMetaTextStyle}
                                                >
                                                  via {item.paymentProcessor}
                                                </span>
                                              )}
                                              {item.depositDate &&
                                                !pendingDeposit && (
                                                  <span
                                                    style={titleMetaTextStyle}
                                                  >
                                                    {`Deposited: ${(() => {
                                                      const depositDate =
                                                        new Date(
                                                          item.depositDate
                                                        );
                                                      return `${(
                                                        depositDate.getUTCMonth() +
                                                        1
                                                      )
                                                        .toString()
                                                        .padStart(
                                                          2,
                                                          "0"
                                                        )}/${depositDate
                                                        .getUTCDate()
                                                        .toString()
                                                        .padStart(
                                                          2,
                                                          "0"
                                                        )}/${depositDate.getUTCFullYear()}`;
                                                    })()}`}
                                                  </span>
                                                )}
                                              {item.isRecurringInstance && (
                                                <span
                                                  style={{
                                                    ...tagStyle,
                                                    backgroundColor:
                                                      "#FED7D720",
                                                    color: "#F56565",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                  }}
                                                >
                                                  <EventRepeatIcon
                                                    sx={{ fontSize: 14 }}
                                                  />
                                                  Recurring Instance
                                                </span>
                                              )}
                                              {item.isRecurring &&
                                                !item.isRecurringInstance && (
                                                  <span
                                                    style={{
                                                      ...tagStyle,
                                                      backgroundColor:
                                                        "#10B98120",
                                                      color: "#10B981",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <RepeatIcon
                                                      sx={{ fontSize: 14 }}
                                                    />
                                                    {formatRecurrenceLabel(
                                                      item.recurrencePattern
                                                    )}
                                                  </span>
                                                )}
                                              {(
                                                item as Income & {
                                                  is_shared?: boolean;
                                                }
                                              ).is_shared || item.isShared ? (
                                                <span style={sharedTagStyle}>
                                                  Shared
                                                </span>
                                              ) : null}
                                              <span
                                                style={{
                                                  ...tagStyle,
                                                  backgroundColor: `${sectionColors[section]}20`,
                                                  color: sectionColors[section],
                                                  marginLeft: "auto",
                                                }}
                                              >
                                                Owed:{" "}
                                                {incomeUtils.formatCurrency(
                                                  incomeUtils.calculateOwed(
                                                    item
                                                  )
                                                )}
                                              </span>
                                              <span
                                                style={{
                                                  ...tagStyle,
                                                  backgroundColor: `${sectionColors[section]}20`,
                                                  color: sectionColors[section],
                                                }}
                                              >
                                                Paid:{" "}
                                                {incomeUtils.formatCurrency(
                                                  item.paidToDate
                                                )}
                                              </span>
                                            </span>
                                            <span
                                              className="income-item-actions"
                                              style={titleRightIconsStyle}
                                            >
                                              {pendingDeposit &&
                                                !isDepositGhost && (
                                                  <span
                                                    title="This payment has been received but not deposited."
                                                    style={{
                                                      ...actionButtonStyle,
                                                      color: "#F56565",
                                                      cursor: "default",
                                                    }}
                                                  >
                                                    <ErrorOutlineIcon
                                                      sx={{ fontSize: 18 }}
                                                    />
                                                  </span>
                                                )}
                                              {canInteract &&
                                                !item.receivedDate &&
                                                renderActionButton(
                                                  "Mark as Received",
                                                  () => onMarkReceived(item),
                                                  <CheckCircleIcon
                                                    sx={{ fontSize: 18 }}
                                                  />,
                                                  "#10B981"
                                                )}
                                              {canInteract &&
                                                renderActionButton(
                                                  "Edit",
                                                  () => onEditIncome(item),
                                                  <EditIcon
                                                    sx={{ fontSize: 18 }}
                                                  />,
                                                  "#4299E1"
                                                )}
                                              {canInteract &&
                                                (item.isRecurring ||
                                                  item.isRecurringInstance) &&
                                                renderActionButton(
                                                  "Skip this occurrence",
                                                  () => onSkipIncome(item),
                                                  <SkipNextIcon
                                                    sx={{ fontSize: 18 }}
                                                  />,
                                                  "#4299E1"
                                                )}
                                              {canInteract &&
                                                onRescheduleIncome &&
                                                renderActionButton(
                                                  "Move to a later date",
                                                  () =>
                                                    onRescheduleIncome(item),
                                                  <EditCalendarIcon
                                                    sx={{ fontSize: 18 }}
                                                  />,
                                                  "#4299E1"
                                                )}
                                              {canInteract &&
                                                onSuspendIncome &&
                                                renderActionButton(
                                                  incomeUtils.isIncomeSuspended(
                                                    item
                                                  )
                                                    ? "Resume"
                                                    : "Postpone",
                                                  () => onSuspendIncome(item),
                                                  incomeUtils.isIncomeSuspended(
                                                    item
                                                  ) ? (
                                                    <PlayCircleOutlineIcon
                                                      sx={{ fontSize: 18 }}
                                                    />
                                                  ) : (
                                                    <PauseCircleOutlineIcon
                                                      sx={{ fontSize: 18 }}
                                                    />
                                                  ),
                                                  "#D69E2E"
                                                )}
                                              {canInteract &&
                                                onShareIncome &&
                                                renderActionButton(
                                                  "Share",
                                                  () => onShareIncome(item),
                                                  <ShareIcon
                                                    sx={{ fontSize: 18 }}
                                                  />,
                                                  "#4299E1"
                                                )}
                                              {canInteract &&
                                                renderActionButton(
                                                  "Delete",
                                                  () => onDeleteIncome(item),
                                                  <DeleteIcon
                                                    sx={{ fontSize: 18 }}
                                                  />,
                                                  "#E53E3E"
                                                )}
                                              <span
                                                style={{
                                                  fontWeight: "bold",
                                                  marginLeft: "6px",
                                                  color:
                                                    section === "received"
                                                      ? isDarkMode
                                                        ? "rgba(226, 232, 240, 0.65)"
                                                        : "rgba(45, 55, 72, 0.65)"
                                                      : "#10B981",
                                                }}
                                              >
                                                {incomeUtils.formatCurrency(
                                                  item.invoiceTotal
                                                )}
                                              </span>
                                            </span>
                                          </span>
                                          {item.notes && (
                                            <div
                                              className="tooltip-content"
                                              style={{
                                                position: "absolute",
                                                top: "calc(100% + 5px)",
                                                left: "0",
                                                backgroundColor: isDarkMode
                                                  ? "#2d2d2d"
                                                  : "white",
                                                padding: "8px 12px",
                                                borderRadius: "6px",
                                                boxShadow: isDarkMode
                                                  ? "0 2px 10px rgba(0,0,0,0.5)"
                                                  : "0 2px 10px rgba(0,0,0,0.2)",
                                                zIndex: 1000,
                                                width: "250px",
                                                color: isDarkMode
                                                  ? "#e0e0e0"
                                                  : "#2D3748",
                                                fontWeight: "normal",
                                                fontSize: "14px",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              <strong>Notes:</strong>
                                              <br />
                                              {item.notes}
                                            </div>
                                          )}
                                        </div>
                                        {item.invoiceLink && (
                                          <div
                                            style={{
                                              color: "#718096",
                                              fontSize: "14px",
                                            }}
                                          >
                                            <a
                                              href={item.invoiceLink}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={linkButtonStyle}
                                            >
                                              View Invoice
                                            </a>
                                          </div>
                                        )}
                                        {item.dependsOnCompletion && (
                                          <div
                                            style={{
                                              color: "#718096",
                                              fontSize: "14px",
                                            }}
                                          >
                                            Depends on:{" "}
                                            {item.dependsOnCompletion}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default IncomeList;
